import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Eraser, Check, Star, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import StrokeGuide from "@/components/StrokeGuide";
import { hasStrokes } from "@/data/strokes";
import useTracingCanvas from "@/hooks/useTracingCanvas";

const FONT_SIZE_MAP = {
  sm: "text-[10rem]",
  md: "text-[12rem]",
  lg: "text-[14rem]",
  xl: "text-[16rem]",
};
const STAR_SLOTS = [1, 2, 3];
const CANVAS_TOUCH_STYLE = { touchAction: "none" };
const GUIDE_W_RATIO = 0.7;
const GUIDE_H_RATIO = 0.8;

export default function TracingExercise() {
  const { id, exerciseId } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [student, setStudent] = useState(null);
  const [completed, setCompleted] = useState(null);
  const [playSignal, setPlaySignal] = useState(0);
  const [guideSize, setGuideSize] = useState(0);

  // Observe tracing-area size and compute a guaranteed-square guide that fits.
  const setAreaRef = useCallback((node) => {
    if (!node) return;
    const compute = () => {
      const r = node.getBoundingClientRect();
      const size = Math.floor(Math.min(r.width * GUIDE_W_RATIO, r.height * GUIDE_H_RATIO));
      setGuideSize(size);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(node);
    node.__guideRO = ro;
  }, []);

  useEffect(() => {
    api.getExercise(exerciseId).then(setExercise);
    api.getStudent(id).then(setStudent);
  }, [id, exerciseId]);

  const strokeColor = student?.profile?.stroke_color || "#FF7A00";
  const trace = useTracingCanvas({ strokeColor, resetKey: exerciseId });

  const submit = async () => {
    try {
      const rec = await api.postProgress({
        student_id: id,
        exercise_id: exerciseId,
        accuracy: trace.coverage,
        duration_seconds: trace.duration(),
        avg_pressure: trace.averagePressure(),
      });
      setCompleted(rec);
      toast.success(`Great job! You earned ${rec.stars} star${rec.stars > 1 ? "s" : ""}!`);
    } catch {
      toast.error("Could not save progress");
    }
  };

  if (!exercise) return <div className="p-10">Loading…</div>;

  const guideFont = student?.profile?.guide_text_size || "lg";
  const isLong = exercise.guide_text.length > 4;
  const guideHasStrokes = hasStrokes(exercise.guide_text);

  return (
    <div className="student-view min-h-screen bg-background flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between border-b border-border">
        <button
          onClick={() => navigate(`/student/${id}`)}
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
          data-testid="exit-exercise-btn"
        >
          <ArrowLeft className="h-5 w-5" /> Back
        </button>
        <div className="font-black text-xl">{exercise.title}</div>
        <div className="text-sm text-muted-foreground" data-testid="coverage-readout">
          Coverage: {trace.coverage}%
        </div>
      </header>

      <main className="flex-1 p-4 lg:p-8 flex flex-col items-center">
        <p className="text-center text-muted-foreground mb-4 text-lg">
          Use your stylus or finger to trace over the gray letters below.
        </p>

        <div
          ref={setAreaRef}
          className="relative w-full max-w-4xl flex-1 min-h-[400px] rounded-3xl bg-white border-2 border-dashed border-border overflow-hidden"
          data-testid="tracing-area"
        >
          <div className="absolute inset-0 grid place-items-center pointer-events-none">
            {guideHasStrokes ? (
              <div
                ref={trace.guideRef}
                style={{ width: guideSize, height: guideSize }}
                className="text-foreground/60"
                data-testid="stroke-guide"
              >
                <StrokeGuide guideText={exercise.guide_text} playSignal={playSignal} />
              </div>
            ) : (
              <div
                ref={trace.guideRef}
                className={`trace-guide ${isLong ? "text-7xl sm:text-8xl px-6 text-center break-words" : FONT_SIZE_MAP[guideFont]} leading-none`}
              >
                {exercise.guide_text}
              </div>
            )}
          </div>
          <canvas
            ref={trace.setCanvasRef}
            data-testid="tracing-canvas"
            className="absolute inset-0 w-full h-full touch-none"
            style={CANVAS_TOUCH_STYLE}
            onPointerDown={trace.canvasHandlers.onPointerDown}
            onPointerMove={trace.canvasHandlers.onPointerMove}
            onPointerUp={trace.canvasHandlers.onPointerUp}
            onPointerLeave={trace.canvasHandlers.onPointerLeave}
          />
        </div>

        <div className="mt-6 flex flex-wrap gap-3 items-center justify-center">
          {guideHasStrokes && (
            <button
              onClick={() => setPlaySignal((n) => n + 1)}
              data-testid="watch-strokes-btn"
              className="rounded-full bg-sky-100 text-sky-800 border border-sky-200 px-6 py-4 font-bold inline-flex items-center gap-2 hover:bg-sky-200 min-h-[56px]"
            >
              <PlayCircle className="h-5 w-5" /> Watch me trace!
            </button>
          )}
          <button
            onClick={trace.clear}
            data-testid="clear-canvas-btn"
            className="rounded-full bg-white border border-border px-6 py-4 font-bold inline-flex items-center gap-2 hover:border-orange-500 min-h-[56px]"
          >
            <Eraser className="h-5 w-5" /> Clear
          </button>
          <button
            onClick={submit}
            disabled={!trace.hasDrawn}
            data-testid="submit-tracing-btn"
            className="rounded-full bg-orange-500 disabled:bg-muted disabled:text-muted-foreground text-white px-8 py-4 font-black inline-flex items-center gap-2 hover:bg-orange-600 min-h-[56px]"
          >
            <Check className="h-5 w-5" /> Done!
          </button>
        </div>
      </main>

      {completed && (
        <SuccessModal
          completed={completed}
          onTryAgain={() => {
            setCompleted(null);
            trace.clear();
          }}
          onContinue={() => navigate(`/student/${id}`)}
        />
      )}
    </div>
  );
}

function SuccessModal({ completed, onTryAgain, onContinue }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" data-testid="success-modal">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center animate-pop-in">
        <div className="mx-auto h-20 w-20 rounded-full bg-orange-100 grid place-items-center mb-4">
          <Star className="h-10 w-10 text-orange-500 fill-orange-500" />
        </div>
        <h2 className="text-3xl font-black mb-2">Awesome work!</h2>
        <div className="flex justify-center gap-1 my-4">
          {STAR_SLOTS.map((s) => (
            <Star
              key={s}
              className={`h-12 w-12 ${s <= completed.stars ? "fill-orange-500 text-orange-500 animate-pop-in" : "text-muted"}`}
              style={{ animationDelay: `${s * 0.15}s` }}
            />
          ))}
        </div>
        <p className="text-muted-foreground">
          Accuracy: {completed.accuracy}% · Time: {completed.duration_seconds}s
        </p>
        <div className="mt-6 flex gap-3 justify-center">
          <button
            onClick={onTryAgain}
            className="rounded-full bg-white border border-border px-6 py-3 font-bold"
            data-testid="try-again-btn"
          >
            Try again
          </button>
          <button
            onClick={onContinue}
            className="rounded-full bg-orange-500 text-white px-6 py-3 font-bold"
            data-testid="continue-btn"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}
